import { useCallback, useMemo } from 'react';
import { TaxIdElement } from '@stripe/react-stripe-js';
import type {
  StripeTaxIdElementChangeEvent,
  StripeTaxIdElementOptions,
  TaxIdType,
} from '@stripe/stripe-js';

/**
 * Tax ID value mapped to the shape the massdriver `UpdateAccountBilling`
 * mutation expects: `taxType` is the Stripe customer tax-id type
 * (`eu_vat`, `gb_vat`, …), which is the element's `externalTaxIdType`.
 */
export type TaxIdValue = {
  taxId: string;
  taxType: string;
};

type StripeTaxIdElementProps = {
  defaultTaxId?: string;
  defaultTaxIdType?: TaxIdType;
  onChange: (value: TaxIdValue | null, complete: boolean) => void;
};

export default function StripeTaxIdElement({
  defaultTaxId,
  defaultTaxIdType,
  onChange,
}: StripeTaxIdElementProps) {
  const handleChange = useCallback(
    (event: StripeTaxIdElementChangeEvent) => {
      if (event.empty) {
        onChange(null, event.complete);
        return;
      }

      onChange(
        {
          taxId: event.value.taxId,
          taxType: event.value.externalTaxIdType,
        },
        event.complete
      );
    },
    [onChange]
  );

  const options = useMemo<StripeTaxIdElementOptions>(
    () => ({
      fields: { businessName: 'never' },
      visibility: 'always',
      defaultValues:
        defaultTaxId && defaultTaxIdType
          ? { taxId: defaultTaxId, taxIdType: defaultTaxIdType }
          : undefined,
    }),
    [defaultTaxId, defaultTaxIdType]
  );

  return <TaxIdElement onChange={handleChange} options={options} />;
}
